import Bugsnag, { BrowserConfig, Event } from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';

import {
  APP_VERSION,
  BUGSNAG_API_KEY,
  BUGSNAG_NOTIFY_ENDPOINT,
  BUGSNAG_SESSION_ENDPOINT,
  isClient,
  isLocalDevelopment,
  isProductionRelease,
  isTestEnvironment,
  RELEASE_STAGE,
  ReleaseStage,
  releaseStage,
} from ':fat/init/env';

// Configuration
const enabledReleaseStages = [ReleaseStage.production, ReleaseStage.development];
const isBugsnagEnabled = Boolean(BUGSNAG_API_KEY) && enabledReleaseStages.includes(releaseStage);

const config: BrowserConfig = {
  apiKey: BUGSNAG_API_KEY,
  appVersion: APP_VERSION,
  plugins: [new BugsnagPluginReact()],
  endpoints: {
    notify: BUGSNAG_NOTIFY_ENDPOINT,
    sessions: BUGSNAG_SESSION_ENDPOINT,
  },
  releaseStage: RELEASE_STAGE,
  enabledReleaseStages: ['production', 'development'],
};

// Types
type LogErrorMetadata = {
  name: string;
  data: Record<string, unknown> | Error;
}[];

export type LogErrorData = {
  context?: string;
  severity?: Event['severity'];
  metadata?: LogErrorMetadata;
};

// Functions
function reportError(error: string | Error, data?: LogErrorData) {
  if (!isBugsnagEnabled || !data) {
    return;
  }

  Bugsnag.notify(error, (event: Event) => {
    const { context, severity, metadata } = data;

    // Log errors on the server side, regardless of the environment.
    // Do not log client errors on the production environment.
    if (!isClient || (isClient && !isProductionRelease)) {
      // eslint-disable-next-line
      console.error("[Bugsnag'd error]", error);
    }

    if (metadata) {
      metadata.forEach((tab) => event.addMetadata(tab.name, tab.data));
    }

    // eslint-disable-next-line no-param-reassign
    event.context = context;

    // eslint-disable-next-line no-param-reassign
    event.severity = severity ?? 'error';
  });
}

function bugsnagStart() {
  // Switch off logging during local development or unit/e2e tests
  if (isLocalDevelopment || isTestEnvironment) {
    Object.assign(config, { logger: null });
  }

  if (isBugsnagEnabled) {
    Bugsnag.start(config);
  }
}

export { Bugsnag, bugsnagStart, reportError };
